"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Sparkles,
  Search,
  Bell,
  Settings,
  User,
  ChevronDown,
  Building2,
  HelpCircle,
  LogOut,
  Layout,
  Monitor,
  BrainCircuit,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { UserSettingsDialog } from "./user-settings-dialog";
import { TenantSwitchDialog } from "./tenant-switch-dialog";
import { useAuth } from "@/contexts/auth-context";

interface TopBarProps {
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
  onLogout?: () => void;
}

export function TopBar({ onToggleSidebar: _onToggleSidebar, sidebarCollapsed: _sidebarCollapsed, onLogout }: TopBarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, tenant } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTenantSwitch, setShowTenantSwitch] = useState(false);
  const [activeTier, setActiveTier] = useState<string>(() => {
    if (typeof window !== "undefined") return localStorage.getItem("sidebar_tier") || "workbench";
    return "workbench";
  });

  const tierDefs = [
    { id: "workbench",      label: "开阳·工作台",   icon: Layout,       color: "#22d3ee", desc: "日常办公 · 企管直达" },
    { id: "tenant-console", label: "天权·分控台",   icon: Monitor,      color: "#3b82f6", desc: "租户管理 · 门户配置" },
    { id: "admin-console",  label: "天枢·总控台",   icon: BrainCircuit, color: "#8b5cf6", desc: "平台运维 · 业务监控" },
  ];

  const switchTier = (t: string) => {
    setActiveTier(t);
    if (typeof window !== "undefined") {
      localStorage.setItem("sidebar_tier", t);
      window.dispatchEvent(new CustomEvent("tier-change", { detail: t }));
    }
    setShowUserMenu(false);
  };

  // Get current page title based on pathname
  const getPageTitle = () => {
    if (pathname === "/") return "平台总览";
    // 灵钛独立路由
    if (pathname.startsWith("/E/")) return "开放平台·灵钛";
    // 工作台 (dashboard)
    if (pathname.startsWith("/dashboard/linglong")) return "灵珑·H5工作台";
    if (pathname.startsWith("/dashboard/lingyi")) return "灵翼·APP工作台";
    if (pathname.startsWith("/dashboard/linglu")) return "灵鹿·小程序工作台";
    if (pathname.startsWith("/dashboard/workbench") || pathname.startsWith("/dashboard")) return "灵犀·PC工作台";
    // 分控台 (tenant) - 具体路径优先
    if (pathname.startsWith("/tenant/linghe-tenant/")) return "灵核·租户业务";
    if (pathname.startsWith("/tenant/lingyu")) return "灵玉·三方集成";
    if (pathname.startsWith("/tenant/lingquan/")) return "灵泉·数据中枢";
    if (pathname.startsWith("/tenant/lingmen-tenant/")) return "灵门·租户门户";
    if (pathname.startsWith("/tenant/overview") || pathname.startsWith("/tenant/tenant") || pathname.startsWith("/tenant/role") || pathname.startsWith("/tenant/user") || pathname.startsWith("/tenant/employee") || pathname.startsWith("/tenant/org") || pathname.startsWith("/tenant/permission") || pathname.startsWith("/tenant/security") || pathname.startsWith("/tenant/settings") || pathname.startsWith("/tenant/subscription") || pathname.startsWith("/tenant/app") || pathname.startsWith("/tenant/data") || pathname.startsWith("/tenant/docs") || pathname.startsWith("/tenant/mail")) return "灵耀·租户自治";
    if (pathname.startsWith("/tenant")) return "天权·分控台";
    // 总控台 (admin) - 具体路径优先
    if (pathname.startsWith("/admin/governance")) return "平台治理";
    if (pathname.startsWith("/admin/auth")) return "权限中心";
    if (pathname.startsWith("/admin/linghe-admin")) return "灵核·管业务";
    if (pathname.startsWith("/admin/lingzhi/audit")) return "安全审计";
    if (pathname.startsWith("/admin/lingzhi/security")) return "系统安全";
    if (pathname.startsWith("/admin/lingzhi/")) return "灵智·运维监控";
    if (pathname.startsWith("/admin/yaoguang")) return "瑶光·AI赋能";
    if (pathname.startsWith("/admin/tianchi/docs")) return "文档管理";
    if (pathname.startsWith("/admin/tianchi/mail")) return "灵鹊";
    if (pathname.startsWith("/admin/tianchi/")) return "天池·基础设施";
    if (pathname.startsWith("/admin/lingmen-admin/")) return "灵门·平台门户";
    if (pathname.startsWith("/admin")) return "天枢·总控台";
    // 登录
    if (pathname.startsWith("/login")) return "登录";
    return "天枢超级底座";
  };

  return (
    <header className="h-14 border-b border-border/50 bg-white/95 backdrop-blur-sm flex items-center px-4 shrink-0 z-50">
      {/* Left: Logo */}
      <div className="flex items-center gap-2.5">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5">
          <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-stellar-gold/20 to-stellar-gold/5 border border-stellar-gold/30">
            <Sparkles className="w-4 h-4 text-stellar-gold" />
            <div className="absolute inset-0 rounded-lg bg-stellar-gold/10 animate-pulse-glow" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gradient-gold tracking-wide leading-tight">
              天枢 · 超级底座
            </span>
            <span className="text-[9px] text-muted-foreground tracking-wider leading-tight">
              TIANSHU SUPER BASE
            </span>
          </div>
        </Link>

        {/* Divider */}
        <div className="w-px h-6 bg-border/50 mx-2" />

        {/* Current Page */}
        <span className="text-sm text-foreground/80 font-medium">
          {getPageTitle()}
        </span>
      </div>

      {/* Center: Search */}
      <div className="flex-1 flex justify-center px-8">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="搜索功能、租户、应用..."
            className="w-full h-9 pl-9 pr-4 rounded-lg bg-gray-50 border border-transparent focus:border-stellar-gold/30 focus:bg-white focus:outline-none text-sm transition-all placeholder:text-muted-foreground/60"
          />
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1">
        {/* Notifications */}
        <button className="relative p-2 rounded-lg hover:bg-gray-100 text-muted-foreground hover:text-foreground transition-colors">
          <Bell className="w-4.5 h-4.5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
        </button>

        {/* Settings */}
        <button className="p-2 rounded-lg hover:bg-gray-100 text-muted-foreground hover:text-foreground transition-colors">
          <Settings className="w-4.5 h-4.5" />
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-border/50 mx-1" />

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-stellar-gold/30 to-stellar-gold/10 border border-stellar-gold/20 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-stellar-gold" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-foreground leading-tight">
                {user?.displayName || "管理员"}
              </span>
              <span className="text-[10px] text-muted-foreground leading-tight">
                {tenant?.name || "系统管理"}
              </span>
            </div>
            <ChevronDown className={cn(
              "w-3.5 h-3.5 text-muted-foreground transition-transform",
              showUserMenu && "rotate-180"
            )} />
          </button>

          {/* User Dropdown */}
          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-border/50 py-1 z-50">
                <div className="px-3 py-2 border-b border-border/30">
                  <p className="text-sm font-medium">{user?.displayName || "管理员"}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || "admin@huawei.com"}</p>
                </div>
                {/* 工作台 / 分控台 / 控制台 切换 */}
                <div className="px-2 py-1.5 border-b border-border/30">
                  <p className="text-[10px] text-muted-foreground px-1 pb-1 font-medium tracking-wider">控制台切换</p>
                  {tierDefs.map((tier) => {
                    const Icon = tier.icon;
                    const isActive = activeTier === tier.id;
                    return (
                      <button
                        key={tier.id}
                        onClick={() => switchTier(tier.id)}
                        className={cn(
                          "w-full px-2 py-1.5 rounded-md text-left text-sm flex items-center gap-2 transition-colors mb-0.5",
                          isActive ? "bg-gray-100 font-medium" : "hover:bg-gray-50 text-muted-foreground"
                        )}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: tier.color }} />
                        <div className="flex-1">
                          <span className="text-xs">{tier.label}</span>
                          <span className="text-[10px] text-muted-foreground ml-1.5">{tier.desc}</span>
                        </div>
                        {isActive && <Check className="w-3 h-3 text-stellar-gold" />}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => { setShowSettings(true); setShowUserMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                >
                  个人设置
                </button>
                <button
                  onClick={() => { setShowTenantSwitch(true); setShowUserMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                >
                  切换租户
                </button>
                <button className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors">
                  帮助文档
                </button>
                <div className="border-t border-border/30 my-1" />
                <button
                  onClick={() => { onLogout?.(); router.push("/login"); }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  退出登录
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <UserSettingsDialog open={showSettings} onClose={() => setShowSettings(false)} />
      <TenantSwitchDialog open={showTenantSwitch} onClose={() => setShowTenantSwitch(false)} />
    </header>
  );
}
